import React, { useState, useEffect } from 'react';
import {
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UsersIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { apiRequest } from '../../utils/apiUtils';
import CreateTeamModal from '../../components/teams/CreateTeamModal';
import ManageTeamMembersModal from '../../components/teams/ManageTeamMembersModal';

const AdminTeamsPage = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamToDelete, setTeamToDelete] = useState(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/teams');
      if (response.success && response.teams) {
        setTeams(Array.isArray(response.teams) ? response.teams : []);
      } else {
        setTeams([]);
        setError(response.error || 'Failed to load teams');
      }
    } catch (err) {
      setTeams([]);
      setError(err.message || 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async (teamData) => {
    try {
      const response = await apiRequest('/teams', {
        method: 'POST',
        data: teamData
      });

      if (response.success) {
        setTeams([...teams, response.data]);
        setShowCreateModal(false);
        return { success: true };
      } else {
        return { success: false, error: response.error };
      }
    } catch (err) {
      // Extract error message from response if available
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create team';
      return { success: false, error: errorMessage };
    }
  };

  const handleUpdateTeam = async (teamId, teamData) => {
    try {
      const response = await apiRequest(`/teams/${teamId}`, {
        method: 'PUT',
        data: teamData
      });

      if (response.success) {
        setTeams(teams.map(t => t.id === teamId ? response.data : t));
        return { success: true };
      } else {
        return { success: false, error: response.error };
      }
    } catch (err) {
      // Extract error message from response if available
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update team';
      return { success: false, error: errorMessage };
    }
  };

  const handleDeleteTeam = async () => {
    if (!teamToDelete) return;

    try {
      const response = await apiRequest(`/teams/${teamToDelete.id}`, {
        method: 'DELETE'
      });

      if (response.success) {
        setTeams(teams.filter(t => t.id !== teamToDelete.id));
        setShowDeleteModal(false);
        setTeamToDelete(null);
      } else {
        setError(response.error || 'Failed to delete team');
      }
    } catch (err) {
      // Extract error message from response if available
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete team';
      setError(errorMessage);
    }
  };

  const handleManageMembers = (team) => {
    setSelectedTeam(team);
    setShowMembersModal(true);
  };

  const handleMembersUpdated = async (updatedTeam) => {
    // If updatedTeam data is provided, update only that team in state
    if (updatedTeam) {
      setTeams(teams.map(t => t.id === updatedTeam.id ? updatedTeam : t));
      // Also update selectedTeam to prevent re-render issues
      setSelectedTeam(updatedTeam);
    } else {
      // Fallback: refresh all teams
      await fetchTeams();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-medium text-secondary-900">Teams</h2>
          <p className="mt-1 text-sm text-secondary-500">
            Manage teams and their members for collaboration and issue assignment.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Team
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      {/* Teams Grid */}
      {!teams || teams.length === 0 ? (
        <div className="text-center py-12 bg-secondary-50 rounded-lg">
          <UserGroupIcon className="mx-auto h-12 w-12 text-secondary-400" />
          <h3 className="mt-2 text-sm font-medium text-secondary-900">No teams</h3>
          <p className="mt-1 text-sm text-secondary-500">
            Get started by creating a new team.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Team
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              onEdit={() => {
                setSelectedTeam(team);
                setShowCreateModal(true);
              }}
              onDelete={() => {
                setTeamToDelete(team);
                setShowDeleteModal(true);
              }}
              onManageMembers={() => handleManageMembers(team)}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Team Modal */}
      {showCreateModal && (
        <CreateTeamModal
          team={selectedTeam}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedTeam(null);
          }}
          onSave={selectedTeam ? handleUpdateTeam : handleCreateTeam}
        />
      )}

      {/* Manage Members Modal */}
      {showMembersModal && selectedTeam && (
        <ManageTeamMembersModal
          team={selectedTeam}
          onClose={() => {
            setShowMembersModal(false);
            setSelectedTeam(null);
          }}
          onMembersUpdated={handleMembersUpdated}
        />
      )}

      {/* Delete Team Confirmation Modal */}
      {showDeleteModal && teamToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowDeleteModal(false)}>
              <div className="absolute inset-0 bg-secondary-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-secondary-900">Delete Team</h3>
                    <div className="mt-2">
                      <p className="text-sm text-secondary-500">
                        Are you sure you want to delete <strong>{teamToDelete.name}</strong>?
                        All team members will be removed. This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-secondary-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteTeam}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Delete Team
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setTeamToDelete(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-secondary-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Team Card Component
const TeamCard = ({ team, onEdit, onDelete, onManageMembers }) => {
  const iconStyle = {
    backgroundColor: team.color + '20', // 20% opacity
    color: team.color
  };

  return (
    <div className="relative bg-white border border-secondary-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      {/* Team Icon and Name */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center">
          <div
            className="h-10 w-10 rounded-lg flex items-center justify-center mr-3"
            style={iconStyle}
          >
            <UserGroupIcon className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-secondary-900">{team.name}</h3>
            <p className="text-xs text-secondary-500 flex items-center mt-1">
              <UsersIcon className="h-4 w-4 mr-1" />
              {team.member_count} {team.member_count === 1 ? 'member' : 'members'}
            </p>
          </div>
        </div>
      </div>

      {/* Description */}
      {team.description && (
        <p className="text-sm text-secondary-600 mb-3 line-clamp-2">
          {team.description}
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-secondary-100">
        <button
          onClick={onManageMembers}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          Manage Members
        </button>
        <div className="flex items-center space-x-2">
          <button
            onClick={onEdit}
            className="p-1 text-secondary-400 hover:text-secondary-600"
            title="Edit team"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={onDelete}
            className="p-1 text-secondary-400 hover:text-red-600"
            title="Delete team"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminTeamsPage;
